/**
 * One Automations card — icon, name, one-line description, on/off toggle, a "last run" line that
 * deep-links into the Activity Center, and a "Customize ›" affordance. There is no create/delete
 * affordance: the four cards are fixed presets seeded by the backend (design 2026-06-26).
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { Automation, PresetKind } from "./redesign/fixtures";
import {
  useSetAutomationEnabled,
  useRunningNow,
} from "./redesign/useAutomationsUi";
import { useBriefDock } from "@/app/providers/BriefDockContext";
import { PRESET_META } from "./preset-meta";
import { AutomationToggle } from "./AutomationToggle";
import { AutomationSettings } from "./AutomationSettings";

/** The small check used by both the transient "just finished" flash and the steady "Done" state. */
function DoneCheck() {
  return (
    <svg
      className="autocard__check"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function AutomationCard({
  kind,
  automation,
  loading,
  open = false,
  moduleId,
  onCustomize,
}: {
  kind: PresetKind;
  /** The live preset row for this kind, when seeded/loaded. */
  automation: Automation | undefined;
  loading: boolean;
  /** Whether THIS card is the one expanded in the single-open accordion. */
  open?: boolean;
  /** The owning module pane's instance id — threaded to AutomationSettings so its unsaved-edit
   *  draft stash (sessionStorage) is scoped per module instance, mirroring the open-panel key. */
  moduleId?: string;
  /** Toggle this card's inline settings — open it (collapsing any other), or collapse it. */
  onCustomize: () => void;
}) {
  const meta = PRESET_META[kind];
  const setEnabled = useSetAutomationEnabled();
  const { openBrief } = useBriefDock();

  const id = automation?.id;
  const on = automation?.status === "active";
  const busy = setEnabled.isPending;
  // The roster's first fetch hasn't resolved this card's row yet — its true on/off state is
  // UNKNOWN. Reading `automation?.status` as `false` here draws a confident "off" toggle (grey,
  // no accent) that then flips ON the instant data lands — a flash of wrong content for any
  // automation that's actually enabled (#5570). Render a neutral skeleton instead of guessing;
  // once `loading` clears (or this card's row resolves), fall straight through to the real,
  // server-reported on/off — never a seeded guess that the poll would have to override.
  const unresolved = loading && !automation;

  const toggle = () => {
    if (!id) return;
    setEnabled.mutate({ id, enabled: !on });
  };

  // Live run state — the running-now feed reports in-flight runs keyed by automation id. Gated on
  // `on`: a turned-off (paused) automation is NEVER "running", even if a run was in flight when it
  // was toggled off, or the running-now feed (a separate poll, not invalidated on toggle) hasn't
  // caught up yet. Off ⇒ no "Running" text and no running animation.
  const { data: running = [] } = useRunningNow();
  const isRunning = on && !!id && running.some((r) => r.agentId === id);

  // A pending sign-off / drafted output for THIS automation the user hasn't acted on yet
  // (pendingCount is the per-automation count of pending agent-approvals — the same queue the
  // Activity Center bell badges). Drives the steady "Done — something's waiting for you" state.
  const hasNotification = (automation?.pendingCount ?? 0) > 0;

  // Flash "Done ✓" for a few seconds when a run finishes (running → idle).
  const [justFinished, setJustFinished] = useState(false);
  const wasRunning = useRef(false);
  useEffect(() => {
    // An off (paused) automation is never running and must not flash "Done" just because it was
    // toggled off mid-run — reset and bail so it falls straight through to "Not working right now".
    if (!on) {
      wasRunning.current = false;
      setJustFinished(false);
      return;
    }
    if (isRunning) {
      wasRunning.current = true;
      setJustFinished(false);
    } else if (wasRunning.current) {
      wasRunning.current = false;
      setJustFinished(true);
      const t = setTimeout(() => setJustFinished(false), 4000);
      return () => clearTimeout(t);
    }
  }, [isRunning, on]);

  // Single-open accordion disclosure (Automations v2 §1). The settings region always exists
  // (empty when closed) so aria-controls stays valid; the settings BODY mounts only when open.
  const settingsId = `autocard-settings-${kind}`;
  const customizeBtnRef = useRef<HTMLButtonElement>(null);
  const settingsRegionRef = useRef<HTMLDivElement>(null);
  // Seeded from `open` (not hardcoded false): a session-restored panel can now mount ALREADY open
  // (#5571), and without this seed the mount-time effect run below would read that as a genuine
  // open transition and steal focus into the settings region on every re-entry — e.g. away from the
  // chat input the user was just in. Seeding to the initial `open` value means only a REAL post-mount
  // open/close (a click) is ever treated as a transition.
  const wasOpen = useRef(open);
  useEffect(() => {
    // Move focus into the settings region on open; return it to the Customize control on collapse.
    if (open && !wasOpen.current) {
      settingsRegionRef.current?.focus();
    } else if (!open && wasOpen.current) {
      customizeBtnRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  // Off automations drop the preset accent and fall back to .autocard's neutral grey
  // default, so a card that's not on visibly loses its color.
  return (
    <section
      className={`autocard${on ? " is-on" : ""}`}
      style={{ "--accent": on ? meta.accent : undefined } as CSSProperties}
      aria-busy={loading || undefined}
    >
      <span className="autocard__icon" aria-hidden="true">
        {meta.icon}
      </span>

      <div className="autocard__body">
        <div className="autocard__top">
          <h2 className="autocard__name">{meta.label}</h2>
          {unresolved ? (
            <span className="auto-toggle auto-toggle--pending" aria-hidden="true">
              <span className="skeleton auto-toggle__track auto-toggle__track--pending" />
            </span>
          ) : (
            <AutomationToggle
              on={!!on}
              disabled={!id || busy}
              onToggle={toggle}
              label={`Turn ${meta.label} ${on ? "off" : "on"}`}
            />
          )}
        </div>

        <p className="autocard__desc">{meta.description}</p>

        <div className="autocard__foot">
          {isRunning ? (
            <span
              className="autocard__status autocard__status--running"
              aria-live="polite"
            >
              Running
              <span className="autocard__dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </span>
          ) : justFinished ? (
            <span
              className="autocard__status autocard__status--done"
              aria-live="polite"
            >
              <DoneCheck />
              Done
            </span>
          ) : hasNotification ? (
            <button
              type="button"
              className="autocard__status autocard__status--done autocard__status--notify"
              onClick={() => openBrief()}
              title="Review in the Activity Center"
              aria-live="polite"
            >
              <DoneCheck />
              Done
            </button>
          ) : (
            <span className="autocard__lastrun autocard__lastrun--empty">
              {loading ? "Loading…" : "Not working right now"}
            </span>
          )}

          <button
            ref={customizeBtnRef}
            type="button"
            className="autocard__customize"
            onClick={onCustomize}
            aria-expanded={open}
            aria-controls={settingsId}
          >
            Customize{" "}
            <span className="autocard__customize-chevron" aria-hidden="true">
              ›
            </span>
          </button>
        </div>

        <div
          id={settingsId}
          className={`autocard__expand${open ? " autocard__expand--open" : ""}`}
        >
          <div
            ref={settingsRegionRef}
            className="autocard__expand-inner"
            role="region"
            aria-label={`Customize ${meta.label}`}
            tabIndex={-1}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                onCustomize();
              }
            }}
          >
            {open ? (
              <AutomationSettings
                kind={kind}
                automation={automation}
                moduleId={moduleId}
              />
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
